import { Box, Center, Flex, Spinner, Table, Tbody, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useEffect, useState } from 'react';
import { usePageLocalStorage } from '../../hooks/usePageLocalStorage';
import {
    BINGO_LICENSE,
    LEVIATHAN_LICENSE,
    MARBLES_LICENSE,
    RACE_DAY_LICENSE,
} from '../../service/games';
import {
    getAggregatedScores,
    getLeaderboardRanking,
    leaderboardConfigEntity,
    rankingData,
} from '../../service/leaderboard';
import { getPublicProfiles, getPublicProfilesByTwitchID, UserProfile } from '../../service/social';
import { LeaderboardRow } from './LeaderboardRow';

interface GameLeaderboardCategoryProps {
    gameID: string;
    leaderboardConfig: leaderboardConfigEntity;
}

const isEmpty = (map: Record<string, any>): boolean => Object.keys(map).length === 0;

export const GameLeaderboardCategory: React.FC<GameLeaderboardCategoryProps> = ({
    gameID,
    leaderboardConfig,
}) => {
    const { keycloak } = useKeycloak();

    const [rankData, setRankData] = useState<rankingData[]>([]);
    const [profileMap, setProfileMap] = usePageLocalStorage<{ [id: string]: UserProfile }>(
        'profileMap',
        {},
    );
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            setIsLoading(true);
            let rankingData: rankingData[] = [];

            switch (gameID) {
                case BINGO_LICENSE:
                case LEVIATHAN_LICENSE:
                case MARBLES_LICENSE:
                    rankingData = await getLeaderboardRanking(
                        gameID,
                        leaderboardConfig.leaderboardName,
                        'all',
                        '',
                    );
                    break;
                case RACE_DAY_LICENSE:
                    rankingData = await getAggregatedScores(
                        gameID,
                        leaderboardConfig.leaderboardName === 'Streamers',
                        'all',
                        '4', //Current season
                        '',
                    );
                    break;
            }

            setRankData(rankingData);

            if (rankingData.length === 0) {
                setIsLoading(false);
                return;
            }

            const owners = rankingData.map((rank) => rank.id);

            const profileData = await (gameID === MARBLES_LICENSE
                ? getPublicProfilesByTwitchID(owners)
                : getPublicProfiles(owners));

            setProfileMap(profileData);

            if (isEmpty(profileData)) {
                setIsLoading(false);
                return;
            }
        }

        fetchData();
    }, [gameID, leaderboardConfig]);

    return (
        <Box>
            {isEmpty(profileMap) ? (
                isLoading ? (
                    <Center>
                        <Spinner label={'loading leaderboard data'} />
                    </Center>
                ) : (
                    <Flex className={'leaderboard-data-loading-error'}>
                        <Text>Unable to load leaderboard data.</Text>
                        <Text>Please try again later.</Text>
                    </Flex>
                )
            ) : (
                <Table className="leaderboard-table">
                    <Thead>
                        <Tr>
                            <Th className="leaderboard-heading">RANK</Th>
                            <Th className="leaderboard-heading">USER</Th>
                            <Th className="leaderboard-heading">SCORE</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {rankData.map((rnk, idx) => (
                            <LeaderboardRow
                                key={idx}
                                rankData={rnk}
                                profile={profileMap[rnk.id]}
                                isOddRow={idx % 2 === 1}
                                activeUserId={keycloak.subject || ''}
                            />
                        ))}
                    </Tbody>
                </Table>
            )}
        </Box>
    );
};
